import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { generateId } from '../../../server/services/utils.service';
import type { OrgSpace } from '../../../server/types/org-types';
import { AuthUserService } from '../auth-user.service';
import {
  SearchComponent,
  type SearchDialogComponentData,
} from '../search/search.component';
import { type ListFilter, type ListStage, TaskService } from '../task.service';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-task-list-detail',
  templateUrl: './task-list-detail.component.html',
  styleUrls: ['./task-list-detail.component.scss'],
  standalone: false,
})
export class TaskListDetailComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private utilsService = inject(UtilsService);
  private taskService = inject(TaskService);
  private location = inject(Location);
  private changeDetector = inject(ChangeDetectorRef);
  private titleService = inject(TitleService);

  authUserId: string;
  orgId: string;
  spaceId: string;
  assignedTo?: string | undefined | null;
  tagIds: string[] | null;
  stage: ListStage;
  space: OrgSpace;
  pageTitle: string;
  listHeaderText: string;
  preventDragAndEdit?: boolean;

  listId: string;
  listFilter: ListFilter;

  @ViewChild('listContainer')
  listContainerElement: ElementRef<HTMLTextAreaElement>;
  showFilter = true;
  selectDisplayTaskId: string | undefined = undefined;
  selectFirstInListTaskPathBase: string | undefined = undefined;

  pageState: 'waiting' | 'ready' | 'spaceNotFoundError' | 'error' = 'waiting';

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.orgId = this.route.snapshot.params.orgId;
    this.spaceId = this.route.snapshot.params.spaceId;
    this.selectDisplayTaskId = this.route.snapshot.params.taskId;

    const { userId, tagIds } = this.utilsService.taskFilterQueryParams(
      this.route.snapshot.queryParams
    );
    this.assignedTo = userId;
    this.tagIds = tagIds;

    if (this.assignedTo === null) {
      this.listHeaderText = 'Unassigned';
    } else if (this.assignedTo) {
      const assignedToUser = this.authUserService.getUser({
        orgId: this.orgId,
        userId: this.assignedTo,
      });
      if (assignedToUser) this.listHeaderText = assignedToUser.name;
    } else {
      this.listHeaderText = 'All users';
    }

    const stage = this.route.snapshot.url[4].path;
    if (
      !(
        stage === 'backlog' ||
        stage === 'current' ||
        stage === 'completed' ||
        stage === 'trash'
      )
    ) {
      // TODO redirect to 404
      return;
    }
    this.stage = stage;
    const m = new Map<string, string>();
    m.set('backlog', 'Backlog')
      .set('current', 'Current tasks')
      .set('completed', 'Completed')
      .set('trash', 'Trash');

    this.pageTitle = m.get(this.stage) ?? '';
    this.titleService.set(this.pageTitle);

    this.preventDragAndEdit = stage === 'trash';

    // Hide filter when linking to own current list
    const routeDataSub = this.route.data.subscribe((data) => {
      this.showFilter = !!data.hideFilterData;
    });
    this.subscriptions.add(routeDataSub);

    this.listId = generateId();
    this.listFilter = {
      listStage: stage,
      assignedTo: this.assignedTo,
      tagIds: this.tagIds,
      recentlyCompletedBuffer: undefined,
    };

    const userOrgObj = this.authUserService.getAuthUserOrgObj({});
    const space = userOrgObj.org.spaces.find((s) => s._id === this.spaceId);

    if (!space) {
      this.pageState = 'spaceNotFoundError';
      return;
    }

    this.space = space;

    this.scrollUrlSelectedTaskIntoView();

    // Select first task in list if no taskId is set
    if (!this.selectDisplayTaskId) {
      this.selectFirstInListTaskPathBase =
        '/org/' +
        this.orgId +
        '/space/' +
        this.spaceId +
        '/' +
        this.stage +
        '/task/';
    }
    // Select task and change URL if taskId is set
    const timeOfInit = Date.now();
    const selectSub = this.taskService
      .selectDisplayObservable()
      .subscribe(({ taskId, listId }) => {
        if (this.listId === listId) {
          if (taskId !== this.selectDisplayTaskId) {
            // Remove component then restart with new taskId
            const url = this.router
              .createUrlTree(
                [
                  '/org',
                  this.orgId,
                  'space',
                  this.spaceId,
                  this.stage,
                  'task',
                  taskId,
                ],
                { queryParamsHandling: 'merge' }
              )
              .toString();

            if (Date.now() < timeOfInit + 100) {
              this.location.replaceState(url);
            } else {
              this.location.go(url);
            }

            this.selectDisplayTaskId = undefined;
            this.changeDetector.detectChanges();
            this.selectDisplayTaskId = taskId;
          }
        }
      });
    this.subscriptions.add(selectSub);

    // Remove tasks (used in removing blank tasks)
    const removeSubscription = this.taskService
      .taskRemoves({ orgId: this.orgId, spaceId: this.spaceId })
      .subscribe(({ taskId }) => {
        if (taskId === this.selectDisplayTaskId) {
          this.selectDisplayTaskId = undefined;

          const url = this.router
            .createUrlTree(
              ['/org', this.orgId, 'space', this.spaceId, 'pipeline'],
              { queryParamsHandling: 'merge' }
            )
            .toString();

          this.location.go(url);
        }
      });
    this.subscriptions.add(removeSubscription);

    this.taskService.refreshTaskData({
      orgId: userOrgObj.org._id,
      spaceId: this.spaceId,
    });
  }

  scrollUrlSelectedTaskIntoView() {
    if (!this.selectDisplayTaskId) return;
    const taskId = this.selectDisplayTaskId;
    const { orgId } = this;
    this.taskService.scrollTaskIntoView({ orgId, taskId });
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
    this.changeDetector.detectChanges();
  }

  openSearch() {
    const dialogConfig = new MatDialogConfig();
    const { orgId, spaceId, listId, listFilter } = this;
    const data: SearchDialogComponentData = {
      orgId,
      spaceId,
      listContext: [{ listId, listFilter }],
    };
    dialogConfig.data = data;
    dialogConfig.position = { top: '75px' }; // #filterAutocompleteHeight
    const dialogRef = this.dialog.open(SearchComponent, dialogConfig);

    const sub = dialogRef.afterClosed().subscribe((result) => {
      //
    });
    this.subscriptions.add(sub);
  }
  // End #PageListDuplicate

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
